import {Pattern, match} from 'ts-pattern';

import {BusinessCaseType, PurchaseVehicleType} from '@dms/api/finance';
import {SaleVehicleSellingState} from '@dms/api/vehicle';

import {Nullish} from 'shared/web';

export interface SnippetVisibilityParams {
  businessCaseType: BusinessCaseType | Nullish;
  vehicleType: PurchaseVehicleType | Nullish;
  sellingState: NonNullable<SaleVehicleSellingState> | Nullish;
  permissions: {
    canReadSell: boolean;
    canReadPurchase: boolean;
    canReadBusinessCase: boolean;
    canCreatePurchase: boolean;
  };
  isSaleVehicle: boolean;
}

export interface SnippetVisibilityOutput {
  showSellingVehicle: boolean;
  showSellingBc: boolean;
  showBuyingVehicle: boolean;
  showBuyingBc: boolean;
}

const isVehicleAvailableForSale = (
  sellingState: NonNullable<SaleVehicleSellingState> | Nullish
): boolean => sellingState !== 'not-for-sale';

// Visibility requirements are described here: https://carvago.atlassian.net/browse/T20-109990
// canShow* logic is taken from: libs/dms/modules/vehicle/src/pages/VehicleDetail/hooks/useGetVehicleSnippets.tsx
export const getSnippetVisibility = (params: SnippetVisibilityParams): SnippetVisibilityOutput => {
  const canShowSellingBc =
    params.isSaleVehicle &&
    params.permissions.canReadSell &&
    params.permissions.canReadBusinessCase &&
    isVehicleAvailableForSale(params.sellingState);

  const canShowBuyingBc =
    params.isSaleVehicle &&
    params.permissions.canReadPurchase &&
    params.permissions.canReadBusinessCase &&
    isVehicleAvailableForSale(params.sellingState);

  const visibility = match({bcType: params.businessCaseType, vType: params.vehicleType})
    .with({bcType: 'SELLING', vType: 'BROKERAGE'}, () => ({
      showSellingVehicle: params.permissions.canReadSell,
      showSellingBc: false,
      showBuyingVehicle: false,
      showBuyingBc: false,
    }))
    .with({bcType: 'BUYING', vType: 'BROKERAGE'}, () => ({
      showSellingVehicle: false,
      showSellingBc: canShowSellingBc,
      showBuyingVehicle: params.permissions.canCreatePurchase && params.permissions.canReadPurchase,
      showBuyingBc: false,
    }))
    .with({bcType: 'SELLING', vType: 'IN_STOCK'}, () => ({
      showSellingVehicle: params.permissions.canReadSell,
      showSellingBc: false,
      showBuyingVehicle: false,
      showBuyingBc: params.permissions.canCreatePurchase && canShowBuyingBc,
    }))
    .with({bcType: 'BUYING', vType: 'IN_STOCK'}, () => ({
      showSellingVehicle: false,
      showSellingBc: canShowSellingBc,
      showBuyingVehicle: params.permissions.canCreatePurchase && params.permissions.canReadPurchase,
      showBuyingBc: false,
    }))
    .with({bcType: 'SELLING', vType: Pattern.nullish}, () => ({
      showSellingVehicle: params.permissions.canReadSell,
      showSellingBc: false,
      showBuyingVehicle: false,
      showBuyingBc: false,
    }))
    .with({bcType: 'BUYING', vType: Pattern.nullish}, () => ({
      showSellingVehicle: false,
      showSellingBc: false,
      showBuyingVehicle: params.permissions.canCreatePurchase && params.permissions.canReadPurchase,
      showBuyingBc: false,
    }))
    .with({bcType: Pattern.nullish}, () => ({
      showSellingVehicle: params.permissions.canReadSell,
      showSellingBc: false,
      showBuyingVehicle: params.permissions.canCreatePurchase && params.permissions.canReadPurchase,
      showBuyingBc: false,
    }))
    .otherwise(() => ({
      showSellingVehicle: false,
      showSellingBc: false,
      showBuyingVehicle: false,
      showBuyingBc: false,
    }));

  return visibility;
};
